import React, { createContext, useContext, useEffect, useState } from 'react';
import { ref, off, onValue } from 'firebase/database';
import { database } from 'src/book/src/firebase';
import { useAuth } from 'src/views/authentication/AuthContext';
import axios from 'axios'; // Assuming axios for HTTP requests
import { executeApiRequest } from 'src/book/src/utilities/utilities';
import { apiBaseUrl } from 'src/getConfig';
import Utils from 'src/book/src/data/Utils';
import { useLanguage } from 'src/LanguageContext';
import Papa from 'papaparse';
import { translate } from 'src/translate';
import { DateTime } from 'luxon';
import { paymentMethodLabel } from './PaymentMethodIcon';
import {
  refundNonManagedPayment as refundNonManagedPaymentInDb,
  removeNonManagedPayment as removeNonManagedPaymentInDb,
  saveNonManagedPayment as saveNonManagedPaymentInDb,
  toMillis,
} from './nonManagedPaymentUtils';

const ChargeContext = createContext();

export const ChargeProvider = ({ children }) => {
  const { bizId, user, getCustomer } = useAuth();
  const { language } = useLanguage();

  const [invoices, setInvoices] = useState({});
  const [bizInvoices, setBizInvoices] = useState({});
  const [pendingInvoices, setPendingInvoices] = useState({});
  const [nonManagedInvoices, setNonManagedInvoices] = useState({});

  // Function to charge a user
  const staffChargeUser = async (uid, lineItems) => {
    try {
      console.log(JSON.stringify(lineItems, 2));
      const response = await executeApiRequest(
        `${apiBaseUrl}/staffChargeUser`,
        'POST',
        {
          uid,
          bizId,
          staffUid: user.uid,
          lineItems,
        },
        user.accessToken,
      );

      const { invoiceId, url } = response;

      return { invoiceId, url };
    } catch (error) {
      console.error('Failed to charge user:', error);
      throw error;
    }
  };

  const getAppoitmentsLineItems = (events, currency) => {
    if (!events || events.length === 0) {
      return [];
    }

    const appointmentsLineItmes = events.map((event) => {
      return {
        description: Utils.getEventTitle(event, language, getCustomer(event.uid)?.name),
        quantity: 1,
        unitPrice: event.price,
        currency: currency,
        itemType: 'booking',
        eventId: event.eventId,
      };
    });
    return appointmentsLineItmes;
  };

  const getProductsLineItmes = (products, currency) => {
    if (!products || products.length === 0) {
      return [];
    }

    const productsLineItmes = products.map((product) => {
      return {
        description: Utils.getLanguageOrDefault(product.title),
        quantity: product.quantity || 1,
        unitPrice: product.price,
        currency: currency,
        itemType: 'product',
        prodId: product.id,
      };
    });
    return productsLineItmes;
  };

  const getCustomLineItems = (customProducts, currency) => {
    if (!customProducts || customProducts.length === 0) {
      return [];
    }

    const customLineItmes = customProducts.map((product) => {
      return {
        description: product.description,
        quantity: product.quantity,
        unitPrice: product.price,
        currency: currency,
        itemType: 'custom',
      };
    });
    return customLineItmes;
  };

  const enrichInvoice = (invoice, invoiceId, { isNonManaged = false } = {}) => {
    invoice.id = invoice.id || invoiceId;
    invoice.isNonManaged = isNonManaged;

    if (isNonManaged) {
      invoice.total = invoice.amount_paid || 0;
      invoice.paid = true;
    }

    invoice.time = Math.floor(toMillis(invoice.created) / 1000) || invoice.created;

    if (invoice.amount_refunded > 0) {
      invoice.status = 'refunded';
      invoice.priceRes = invoice.amount_refunded / 100;
      invoice.totalRes = (isNonManaged ? invoice.amount_paid : invoice.total) / 100;
      invoice.time = Math.floor(toMillis(invoice.refundTimestamp) / 1000) || invoice.refundTimestamp;
    } else if (invoice.paid) {
      invoice.status = 'paid';
      invoice.priceRes = invoice.total / 100;
    } else {
      invoice.status = 'paid';
      invoice.priceRes = invoice.total / 100;
    }
  };

  useEffect(() => {
    const fetchInvoices = (path, setInvoicesState, { enrich = true, isNonManaged = false } = {}) => {
      const dbRef = ref(database, path);

      const onValueChange = onValue(
        dbRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            setInvoicesState({});
            return;
          }

          const newInvoicesData = snapshot.val();
          const updatedInvoicesData = {};
          Object.keys(newInvoicesData).forEach((customerId) => {
            updatedInvoicesData[customerId] = {};
            const customerInvoices = newInvoicesData[customerId];
            Object.keys(customerInvoices).forEach((invoiceId) => {
              const invoice = customerInvoices[invoiceId];
              if (enrich) {
                enrichInvoice(invoice, invoiceId, { isNonManaged });
              } else {
                invoice.status = 'pending';
                invoice.priceRes = invoice.total / 100;
                invoice.time = invoice.created;
                invoice.id = invoice.id || invoiceId;
              }
              updatedInvoicesData[customerId][invoiceId] = invoice;
            });
          });
          setInvoicesState(updatedInvoicesData);
        },
        (error) => {
          console.error(error);
        },
      );

      return () => off(dbRef, 'value', onValueChange);
    };

    let unsubscribeBiz;
    let unsubscribePending;
    let unsubscribeNonManaged;

    if (bizId) {
      unsubscribeBiz = fetchInvoices(`/billing/invoices/biz/${bizId}`, setBizInvoices);

      unsubscribePending = fetchInvoices(`/billing/invoices/pending/${bizId}`, setPendingInvoices, {
        enrich: false,
      });

      unsubscribeNonManaged = fetchInvoices(
        `/billing/invoices/nonManaged/${bizId}`,
        setNonManagedInvoices,
        { isNonManaged: true },
      );
    }

    return () => {
      if (unsubscribeBiz) unsubscribeBiz();
      if (unsubscribePending) unsubscribePending();
      if (unsubscribeNonManaged) unsubscribeNonManaged();
    };
  }, [bizId]);

  const getPaymentsTable = () => {
    const allInvoicesResult = [];
    const allInvoices = getAllInvoices();
    const allInvoicesArray = Object.keys(allInvoices);

    const dateOptions = [];

    if (allInvoicesArray.length === 0) return { invoices: [], options: [] };

    for (const customerId of allInvoicesArray) {
      const currentCustomer = getCustomer(customerId);
      for (const currentInvoiceId of Object.keys(allInvoices[customerId])) {
        allInvoices[customerId][currentInvoiceId]['customer'] = currentCustomer
          ? currentCustomer
          : { id: customerId };

        if (allInvoices[customerId][currentInvoiceId].effectiveTimestamp > 0) {
          let dt = DateTime.fromMillis(
            allInvoices[customerId][currentInvoiceId].effectiveTimestamp,
          );

          dt = dt.setZone('Asia/Jerusalem');

          const day = dt.day;
          const month = dt.monthLong; // Get full month name
          const year = dt.year;
          const monthNumber = dt.month; // Numeric month (1-12)
          const dateCategory = day <= 15 ? `${month} ${year}-1/15` : `${month} ${year}-16/31`;

          if (dateOptions.filter((opt) => opt.value === dateCategory).length === 0) {
            dateOptions.push({
              value: dateCategory,
              label: dateCategory,
              day,
              month,
              year,
              monthNumber,
              monthMonthType: day <= 15 ? 1 : 2,
            });
          }

          allInvoices[customerId][currentInvoiceId].dateCategory = dateCategory;
          allInvoices[customerId][currentInvoiceId].month = month;
          allInvoices[customerId][currentInvoiceId].year = year;
          allInvoices[customerId][currentInvoiceId].monthMonthType = day <= 15 ? 1 : 2;
        }

        allInvoicesResult.push(allInvoices[customerId][currentInvoiceId]);
      }
    }

    // Sort options by year (descending), month (descending), and monthMonthType (descending)
    dateOptions.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      if (b.monthNumber !== a.monthNumber) return b.monthNumber - a.monthNumber;
      return b.monthMonthType - a.monthMonthType;
    });

    return { invoices: allInvoicesResult, options: dateOptions };
  };

  const mergeInvoiceMaps = (target, source) => {
    for (const [customerId, customerInvoices] of Object.entries(source || {})) {
      if (!target[customerId]) {
        target[customerId] = {};
      }
      target[customerId] = {
        ...target[customerId],
        ...customerInvoices,
      };
    }
    return target;
  };

  const getAllInvoices = () => {
    const combinedInvoices = mergeInvoiceMaps({ ...bizInvoices }, pendingInvoices);
    return mergeInvoiceMaps(combinedInvoices, nonManagedInvoices);
  };

  const getInvoice = (customerId, invoiceId) => {
    const allInvoices = getAllInvoices();

    return allInvoices[customerId][invoiceId];
  };

  function sortInvoicesByTime(invoices) {
    // Convert object to array
    const entries = Object.entries(invoices);

    // Sort by 'time' attribute
    entries.sort((a, b) => b[1].time - a[1].time);

    // Convert back to object and return
    return Object.fromEntries(entries);
  }

  const getCustomerInvoices = (customerId) => {
    const allInvs = getAllInvoices();
    if (allInvs[customerId]) {
      return sortInvoicesByTime(allInvs[customerId]);
    }
    return null;
  };

  const getInvoicesTotal = (invoices) => {
    let sum = 0;

    for (const invoice of invoices) {
      sum = sum + invoice.priceRes;
    }
    return sum;
  };

  const exportToCsv = (invoices, language) => {
    try {
      const invoicesParsed = invoices.map((invoice) => {
        return {
          [translate('price', language)]: invoice.priceRes.toString(),
          [translate('nameShort', language)]: invoice?.customer?.name,
          [translate('phoneNumber', language)]:
            invoice?.customer?.numberLocal?.toString() ?? invoice?.customer?.number?.toString(),
          [translate('status', language)]: translate(invoice?.status, language),
          [translate('timeTransfer', language)]: Utils.formatUnixTimestamp(
            invoice?.effectiveTimestamp,
            language,
          ),
          [translate('timePayment', language)]: Utils.formatUnixTimestamp(
            invoice?.time * 1000,
            language,
          ),
          [translate('invoice', language)]: invoice?.receipt_url
            ? invoice?.receipt_url
            : invoice?.hosted_invoice_url,
          [translate('paymentType', language)]: invoice?.isNonManaged
            ? paymentMethodLabel(invoice?.paymentMethod, language)
            : 'WATS',
        };
      });

      // invoicesParsed.push({ total: 1000 });

      const csv = Papa.unparse(invoicesParsed, {
        header: true,
        delimiter: ',',
      });

      const BOM = '\uFEFF';
      const csvBlob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);

      return csvUrl;
    } catch (err) {
      return null;
    }
  };

  const saveNonManagedPayment = (args) => saveNonManagedPaymentInDb(args);
  const removeNonManagedPayment = (args) => removeNonManagedPaymentInDb(args);
  const refundNonManagedPayment = (args) => refundNonManagedPaymentInDb(args);

  // const staffRefundUser = async (uid, invoiceId) => {
  //     try {
  //         const response = await axios.post('/api/staffRefundUser', {
  //             uid,
  //             bizId,
  //             staffUid,
  //             invoiceId
  //         });
  //         const invoiceRef = ref(database, `billing/invoices/biz/${bizId}/${uid}/${invoiceId}`);
  //         const invoiceSnapshot = await get(invoiceRef);
  //         if (invoiceSnapshot.exists()) {
  //             const invoiceData = invoiceSnapshot.val();
  //             await update(invoiceRef, {
  //                 amount_refunded: invoiceData.total,
  //                 refundTimestamp: Date.now()
  //             });
  //         }
  //     } catch (error) {
  //         console.error('Failed to refund user:', error);
  //         throw error;
  //     }
  // };

  return (
    <ChargeContext.Provider
      value={{
        staffChargeUser,
        getAppoitmentsLineItems,
        getCustomLineItems,
        getAllInvoices,
        getInvoice,
        getCustomerInvoices,
        getProductsLineItmes,
        getPaymentsTable,
        pendingInvoices,
        bizInvoices,
        nonManagedInvoices,
        exportToCsv,
        getInvoicesTotal,
        saveNonManagedPayment,
        removeNonManagedPayment,
        refundNonManagedPayment,
      }}
    >
      {children}
    </ChargeContext.Provider>
  );
};

export const useCharge = () => useContext(ChargeContext);
